import React from "react";
import { Typography, Box, Chip } from "@mui/material";
import TileCard from "../common/TileCard.tsx";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import type { IntegrationMeta } from "./integrationMetadata.ts";

interface IntegrationCardProps {
  integration: IntegrationMeta;
  isConnected: boolean;
  isLoading: boolean;
  onClick: () => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  isConnected,
  isLoading,
  onClick,
}) => {
  const { Icon, iconSrc, name } = integration;

  return (
    <TileCard onClick={onClick}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            backgroundColor: "rgba(169, 125, 88, 0.1)",
          }}
        >
          {Icon ? (
            <Icon sx={{ fontSize: 24, color: "primary.main" }} />
          ) : iconSrc ? (
            <img src={iconSrc} alt={name} style={{ width: 24, height: 24 }} />
          ) : null}
        </Box>

        {/* Text content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="text.primary"
              noWrap
            >
              {name}
            </Typography>
            {!isLoading && (
              <Chip
                size="small"
                label={
                  isConnected ? (
                    <Trans context="Integration connection status badge">
                      Connected
                    </Trans>
                  ) : (
                    <Trans context="Integration available status badge">
                      Available
                    </Trans>
                  )
                }
                sx={{
                  height: 22,
                  fontSize: "0.75rem",
                  backgroundColor: isConnected
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(0, 0, 0, 0.06)",
                  color: isConnected ? "#2e7d32" : "text.secondary",
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {i18n._(integration.description)}
          </Typography>
        </Box>
      </Box>
    </TileCard>
  );
};

export default IntegrationCard;
